<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <h2>IABF Showcase Lineup</h2>
            <v-btn to="/iabf/admin/pieces/new">
              + Add New Showcase Piece
            </v-btn>
            <v-btn :href="publicLineupUrl" target="_blank">
              ↗ Public Lineup Link
            </v-btn>
            <v-btn outlined small class="ml-2" @click="copyPublicLineupUrl">
              {{ publicLinkCopiedNote || 'Copy Public Link' }}
            </v-btn>
            <v-btn :href="showRunnerUrl" target="_blank" class="ml-2" color="amber darken-2" dark>
              ▶ Show Runner
            </v-btn>
            <v-btn to="/iabf/admin/showcase-gaps" class="ml-2" outlined>
              ⏱ Dancer Gaps
            </v-btn>
            <hr />

            <!-- Pending dancer submissions -->
            <section v-if="pendingPieces.length" class="pending-approvals mb-4">
              <h3 class="pending-title">
                Pending approval
                <span class="pending-count">{{ pendingPieces.length }}</span>
              </h3>
              <p class="pending-lede">
                Dancer-submitted pieces waiting for review, oldest first. Approving adds them to the showcase lineup.
              </p>
              <div
                v-for="p in pendingPieces"
                :key="'pending-' + p.id"
                class="pending-row"
              >
                <div class="pending-main">
                  <strong>{{ p.title || '(untitled)' }}</strong>
                  <span v-if="p.songArtist"> — {{ p.songArtist }}</span>
                  <div class="pending-meta">
                    <span v-if="p.namedDancers || p.programDancer">{{ p.namedDancers || p.programDancer }}</span>
                    <span v-if="p.pieceType"> · {{ p.pieceType }}</span>
                    <span v-if="p.groupSize"> · {{ p.groupSize }}</span>
                    <span v-if="formatSubmittedAt(p)"> · Submitted {{ formatSubmittedAt(p) }}</span>
                    <span v-if="p.submissionNotes"> · Notes: {{ p.submissionNotes }}</span>
                  </div>
                </div>
                <div class="pending-actions">
                  <v-btn
                    small
                    color="primary"
                    :loading="approvingId === p.id"
                    :disabled="!!approvingId"
                    @click="approvePiece(p)"
                  >
                    Approve
                  </v-btn>
                  <v-btn small text :to="'/iabf/admin/pieces/edit/' + p.id">
                    Edit
                  </v-btn>
                </div>
              </div>
            </section>

            <v-btn-toggle v-model="view" mandatory dense class="mb-3">
              <v-btn value="table" small>Table</v-btn>
              <v-btn value="lineup" small>Lineup</v-btn>
            </v-btn-toggle>

            <input
              ref="corpsFileInput"
              type="file"
              accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/wav"
              style="display:none"
              @change="onCorpsFileChosen"
            />

            <!-- Proposed Show Order -->
            <section v-if="view === 'lineup'" class="show-order">
              <div class="show-order-header">
                <h3>Proposed Show Order</h3>
                <span class="total-runtime">Total runtime ~{{ totalRuntimeText }}</span>
                <span class="runtime-breakdown">({{ runtimeBreakdownText }})</span>
                <span class="muted">
                  · {{ assignedPieces.length }} placed · {{ unassignedPieces.length }} awaiting preference
                </span>
                <span v-if="piecesMissingMusic.length > 0" class="missing-music-chip">
                  🎵 {{ piecesMissingMusic.length }} missing music
                  <v-btn
                    v-if="missingMusicEmails.length > 0"
                    x-small
                    outlined
                    class="ml-2"
                    @click="copyMissingMusicEmails"
                  >
                    {{ missingMusicCopiedNote || `↗ Copy ${missingMusicEmails.length} email${missingMusicEmails.length === 1 ? '' : 's'}` }}
                  </v-btn>
                </span>
                <span v-if="orderFrozenAt" class="frozen-chip" :title="`Frozen ${frozenAgoText}`">
                  🔒 Order frozen
                  <v-btn x-small text class="ml-1" :loading="unfreezing" @click="onUnfreezeOrder">Unfreeze</v-btn>
                </span>
                <v-btn v-else small outlined color="primary" class="ml-2" :loading="freezing" @click="onFreezeOrder">
                  🔒 Freeze show order
                </v-btn>
              </div>

              <div class="show-flow">
                <template v-for="(slot, i) in showcaseStructure">
                  <div v-if="slot.type === 'corps'" :key="'c' + i" class="flow-item flow-corps">
                    <div class="corps-row-top">
                      <span class="flow-marker">{{ i + 1 }}</span>
                      <span class="flow-title">{{ slot.name }}</span>
                      <span class="flow-tag">Corps · {{ corpsCastTotal(slot.name) }} dancers</span>
                    </div>
                    <div class="corps-row-info">
                      <div class="corps-choreo">Choreographed by {{ corpsInfo(slot.name).choreographer }}</div>
                      <div class="corps-song-row">
                        <v-text-field
                          :value="(corpsSongs[slot.name] || {}).title"
                          dense
                          hide-details
                          placeholder="Song / piece title"
                          class="corps-song-input"
                          @change="setCorpsSong(slot.name, 'title', $event)"
                        />
                        <span class="corps-song-by">by</span>
                        <v-text-field
                          :value="(corpsSongs[slot.name] || {}).artist"
                          dense
                          hide-details
                          placeholder="Composer / artist"
                          class="corps-song-input"
                          @change="setCorpsSong(slot.name, 'artist', $event)"
                        />
                      </div>
                      <div class="corps-music-section">
                        <div v-for="(track, ti) in corpsTracks(slot.name)" :key="ti" class="corps-track-row">
                          <span class="corps-track-num">{{ ti + 1 }}.</span>
                          <audio :src="track.url" controls class="corps-audio" />
                          <span class="corps-music-filename">{{ track.fileName }}</span>
                          <v-btn x-small icon :disabled="ti === 0" @click="moveCorpsTrack(slot.name, ti, -1)" title="Move up">
                            <v-icon small>mdi-arrow-up</v-icon>
                          </v-btn>
                          <v-btn x-small icon :disabled="ti === corpsTracks(slot.name).length - 1" @click="moveCorpsTrack(slot.name, ti, 1)" title="Move down">
                            <v-icon small>mdi-arrow-down</v-icon>
                          </v-btn>
                          <v-btn x-small text color="error" @click="removeCorpsTrack(slot.name, ti)">Remove</v-btn>
                        </div>
                        <div class="corps-music-row">
                          <template v-if="corpsMusicUploading === slot.name">
                            <v-progress-circular indeterminate size="16" width="2" />
                            <span class="muted ml-2">Uploading…</span>
                          </template>
                          <template v-else>
                            <v-btn small outlined @click="pickCorpsMusic(slot.name)">
                              <v-icon left small>mdi-upload</v-icon>
                              {{ corpsTracks(slot.name).length === 0 ? 'Upload corps music' : 'Add another track' }}
                            </v-btn>
                            <span v-if="corpsTracks(slot.name).length > 1" class="muted ml-2">{{ corpsTracks(slot.name).length }} tracks · plays in order</span>
                          </template>
                          <span v-if="corpsMusicError[slot.name]" class="corps-music-error">{{ corpsMusicError[slot.name] }}</span>
                        </div>
                      </div>
                      <div class="corps-recording-row">
                        <span class="corps-recording-lab">Performance recording</span>
                        <input
                          type="text"
                          class="corps-recording-input"
                          placeholder="https://…"
                          :value="corpsRecordingDraft(corpsInfo(slot.name).field)"
                          @input="onCorpsRecordingInput(corpsInfo(slot.name).field, $event.target.value)"
                        />
                        <v-btn
                          x-small color="primary"
                          :loading="!!recordingSaving['corps:' + corpsInfo(slot.name).field]"
                          :disabled="!corpsRecordingChanged(corpsInfo(slot.name).field)"
                          @click="saveCorpsRecording(corpsInfo(slot.name).field)"
                        >Save</v-btn>
                        <span v-if="recordingSaved['corps:' + corpsInfo(slot.name).field]" class="rec-saved">✓</span>
                      </div>
                      <div v-if="corpsCastTotal(slot.name)" class="corps-cast-groups">
                        <div v-for="group in corpsCasts[slot.name]" :key="group.role || 'default'" class="corps-cast-group">
                          <strong v-if="group.role" class="role-label">{{ group.role }}:</strong>
                          <span class="corps-cast">{{ group.dancers.join(', ') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="slot.type === 'intermission'" :key="'i' + i" class="flow-item flow-intermission">
                    <div class="halftime">{{ halfSummary(1) }} · 1st half</div>
                    <div class="intermission-label">{{ slot.name }}</div>
                    <div class="halftime">2nd half · {{ halfSummary(2) }}</div>
                  </div>
                  <div v-else :key="'b' + i" class="flow-item flow-block">
                    <div class="flow-block-header">
                      <span class="flow-marker">{{ i + 1 }}</span>
                      <span class="flow-title">
                        {{ slot.name }}
                        <span v-if="slot.subtitle" class="flow-subtitle">· {{ slot.subtitle }}</span>
                      </span>
                      <span class="flow-tag">{{ blockSummary(slot.block) || '0 solos' }}</span>
                    </div>
                    <div v-if="piecesInBlock(slot.block).length === 0" class="muted ml-4">No pieces assigned to this block yet.</div>
                    <table v-else class="block-table">
                      <tbody>
                        <tr v-for="(p, idx) in piecesInBlock(slot.block)" :key="p.id" :class="{ 'is-pinned': isPinned(p) }">
                          <td class="col-num">
                            <span v-if="isPinned(p)" class="pinned-marker" title="Pinned to this position">📌</span>
                            {{ idx + 1 }}.
                          </td>
                          <td>
                            <router-link :to="'/iabf/admin/pieces/edit/' + p.id" class="link">
                              {{ programTitleFor(p) }}
                            </router-link>
                            <div class="sub program-line">
                              <strong>{{ programDancerFor(p) }}</strong>
                              <span v-if="teacherCredit(p)"> · {{ teacherCredit(p) }}</span>
                            </div>
                            <div class="tag-row">
                              <span v-if="pieceTypeLabel(p)" class="type-pill" :class="pieceTypeClass(p)">{{ pieceTypeLabel(p) }}</span>
                              <span v-if="competitionLabel(p)" class="comp-pill comp-yes">🏅 {{ competitionLabel(p) }}</span>
                            </div>
                          </td>
                          <td>
                            <span v-if="isCompetition(p)" class="comp-pill comp-yes">Competition</span>
                            <span v-else class="comp-pill comp-no">Non-comp</span>
                          </td>
                          <td>
                            <v-select
                              :value="p.showcaseBlock"
                              :items="blockOptions"
                              dense
                              hide-details
                              class="inline-select"
                              @input="updateAssignment(p.id, { showcaseBlock: $event })"
                            />
                          </td>
                          <td>
                            <v-select
                              :value="p.showcasePlacement"
                              :items="placementOptions"
                              dense
                              hide-details
                              clearable
                              class="inline-select"
                              @input="updateAssignment(p.id, { showcasePlacement: $event })"
                            />
                          </td>
                          <td>
                            <v-text-field
                              :value="p.showcaseOrder"
                              dense
                              hide-details
                              clearable
                              placeholder="auto | # | last"
                              class="pin-input"
                              @change="updateAssignment(p.id, { showcaseOrder: $event })"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>
              </div>

              <div class="pending-section">
                <div class="pending-header">
                  <h4>
                    Pieces awaiting block preference
                    <span class="muted">({{ unassignedPieces.length }})</span>
                  </h4>
                  <v-btn
                    v-if="unassignedEmails.length > 0"
                    small
                    outlined
                    @click="copyUnassignedEmails"
                  >
                    {{ emailCopiedNote || `↗ Copy ${unassignedEmails.length} email${unassignedEmails.length === 1 ? '' : 's'}` }}
                  </v-btn>
                </div>
                <div v-if="unassignedPieces.length === 0" class="muted">All pieces have a block. 🎉</div>
                <table v-else class="block-table">
                  <tbody>
                    <tr v-for="p in unassignedPieces" :key="p.id">
                      <td>
                        <router-link :to="'/iabf/admin/pieces/edit/' + p.id" class="link">
                          {{ p.title || '(untitled)' }}
                        </router-link>
                        <div class="sub">{{ p.songArtist }} · {{ p.namedDancers || '(no dancers)' }}</div>
                      </td>
                      <td>
                        <span v-if="isCompetition(p)" class="comp-pill comp-yes">Competition</span>
                        <span v-else class="comp-pill comp-no">Non-comp</span>
                      </td>
                      <td>
                        <v-select
                          :value="p.showcaseBlock"
                          :items="blockOptions"
                          label="Block"
                          dense
                          hide-details
                          class="inline-select"
                          @input="updateAssignment(p.id, { showcaseBlock: $event })"
                        />
                      </td>
                      <td>
                        <v-select
                          :value="p.showcasePlacement"
                          :items="placementOptions"
                          label="Placement"
                          dense
                          hide-details
                          clearable
                          class="inline-select"
                          @input="updateAssignment(p.id, { showcasePlacement: $event })"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <hr v-if="view === 'lineup'" />
            <input
              ref="pieceMusicInput"
              type="file"
              accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/wav"
              style="display:none"
              @change="onPieceMusicChosen"
            />
            <vue-good-table
              v-if="view === 'table'"
              :columns="columns"
              :rows="tableRows"
              :line-numbers="false"
              :row-style-class="tableRowClass"
              :search-options="{
                enabled: true,
                placeholder: 'Search pieces or dancers',
              }"
              :sort-options="{
                enabled: true,
                initialSortBy: { field: 'showcaseSort', type: 'asc' }
              }"
            >
              <div slot="emptystate">
                No pieces match your search!
              </div>
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field == 'showcaseSort'">
                  {{ formatShowcaseSort(props.row.showcaseSort) }}
                </span>
                <span v-else-if="props.column.field =='edit'">
                  <v-btn
                    v-if="!props.row.isCorps"
                    icon
                    :to="'/iabf/admin/pieces/edit/' + props.row.id"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <span v-else class="muted">—</span>
                </span>
                <span v-else-if="props.column.field == 'title'">
                  {{ props.row.title }}
                  <span v-if="props.row.isCorps" class="type-pill">Corps</span>
                </span>
                <span v-else-if="props.column.field == 'namedDancers'">
                  {{ props.row.isCorps ? 'Corps de Ballet' : listDancerNames(props.row.dancers || []) }}
                </span>
                <span v-else-if="props.column.field == 'musicReceived'">
                  <span v-if="props.row.isCorps">
                    <span v-if="props.row.musicReceived">
                      <v-icon color="green" small>mdi-checkbox-marked-circle</v-icon>
                      {{ props.row.musicFileName || 'Received' }}
                    </span>
                    <span v-else class="muted">Upload in Lineup</span>
                  </span>
                  <span v-else-if="pieceMusicUploading === props.row.id">
                    <v-progress-circular indeterminate size="18" width="2" /> Uploading…
                  </span>
                  <span v-else-if="props.row.musicReceived">
                    <a v-if="props.row.musicUrl" :href="props.row.musicUrl" target="_blank" rel="noopener" class="music-link">
                      <v-icon color="green" small>mdi-checkbox-marked-circle</v-icon>
                      {{ props.row.musicFileName || 'Listen' }}
                    </a>
                    <span v-else>
                      <v-icon color="green" small>mdi-checkbox-marked-circle</v-icon> Received
                    </span>
                    <v-btn icon x-small @click="pickPieceMusic(props.row)" title="Replace music">
                      <v-icon small>mdi-upload</v-icon>
                    </v-btn>
                  </span>
                  <span v-else>
                    <v-btn small outlined @click="pickPieceMusic(props.row)">
                      <v-icon left small>mdi-upload</v-icon>
                      Upload mp3
                    </v-btn>
                  </span>
                  <div v-if="pieceMusicError[props.row.id]" class="upload-error">{{ pieceMusicError[props.row.id] }}</div>
                </span>
                <span v-else-if="props.column.field == 'lengthInMinutes'">
                  {{ props.row.lengthInSeconds ? timeInMinutes(props.row.lengthInSeconds) + ' ' + 'minutes' : 'Not received' }}
                </span>
                <span v-else-if="props.column.field == 'programDancer'">
                  {{ props.row.programDancer ? props.row.programDancer : listDancerNames(props.row.dancers || []) }}
                </span>
                <span v-else-if="props.column.field == 'programTeacher'">
                  {{ props.row.programTeacher ? props.row.programTeacher : 'N/A' }}
                </span>
                <span v-else-if="props.column.field == 'programTitle'">
                  {{ props.row.programTitle ? props.row.programTitle : props.row.title + ' - ' + props.row.songArtist }}
                </span>
                <span v-else-if="props.column.field == 'performanceRecordingUrl'">
                  <input
                    type="text"
                    class="rec-input"
                    placeholder="https://…"
                    :value="recordingDraftFor(props.row)"
                    @input="onRecordingInput(props.row.id, $event.target.value)"
                  />
                  <v-btn
                    x-small color="primary"
                    :loading="!!recordingSaving[props.row.id]"
                    :disabled="!recordingChanged(props.row)"
                    @click="saveRowRecording(props.row)"
                  >Save</v-btn>
                  <span v-if="recordingSaved[props.row.id]" class="rec-saved">✓</span>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style>
  @media (min-width: 701px) {
    .mobile {
      display: none;
    }
  }
  .hidden {
    display:none;
  }

  .theme--light.v-btn {
    color: #267E7D;
  }

  .vgt-table, v-btn {
    font-family: Fanwood Text;
  }

  .vgt-table thead {
    font-size: 1.2em !important;
  }

  .vgt-row-header {
    color: #606266;
    font-size: 22px;
  }

  .clickable {
    cursor:pointer;
  }

  a.clickable {
    color: #6acecd;
    text-decoration: underline;
  }

  h2 {
    font-family: Quattrocento;
    font-weight: 400;
    font-style: normal;
    font-size: 39px;
    letter-spacing: -.02em;
    line-height: 1em;
    text-transform: none;
  }

  p, li {
    font-family: Fanwood Text;
    font-weight: 400;
    font-style: normal;
    font-size: 19px;
    letter-spacing: .01em;
    line-height: 1.7em;
    text-transform: none;
    color: rgba(40,26,23,.6);
    line-height: 1.7em;
    color: rgba(40,26,23,.6);
  }

  hr {
    margin: 12px 0;
  }

  .upload-error {
    color: #c62828;
    font-size: 12px;
    margin-top: 4px;
  }

  .music-link {
    color: #267E7D;
    text-decoration: none;
    font-size: 13px;
  }

  .music-link:hover {
    text-decoration: underline;
  }

  tr.divider {
    background-color: #fbfbfb;
  }

  .name {
    font-size: 1.1em;
    font-weight: 600;
  }

  .more-info {
    font-style: italic;
    color: grey;
  }

  .more-info i.mdi {
    font-size: 1.2em !important;
  }

  .more-info .v-icon__svg {
    height: 20px;
    width: 20px;
  }

  .show-order {
    margin: 12px 0 8px;
  }

  .show-order-header {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 14px;
    margin-bottom: 8px;
  }

  .show-order h3 {
    font-family: Quattrocento, Georgia, serif;
    font-weight: 700;
    font-size: 22px;
    margin: 0;
  }

  .total-runtime {
    font-family: Quattrocento, Georgia, serif;
    font-weight: 700;
    font-size: 15px;
    color: #1a1a1a;
    background: #fff8e1;
    padding: 4px 10px;
    border-radius: 4px;
  }

  .runtime-breakdown {
    font-family: 'Fanwood Text', Georgia, serif;
    font-size: 13px;
    color: #777;
    font-style: italic;
    margin-left: 8px;
  }

  .missing-music-chip {
    display: inline-flex;
    align-items: center;
    background: #ffebee;
    color: #b71c1c;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
  }
  .frozen-chip {
    display: inline-flex;
    align-items: center;
    background: #e8f5e9;
    color: #1b5e20;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    margin-left: 8px;
  }

  .show-order .muted {
    color: #999;
    font-style: italic;
    font-size: 13px;
  }

  .show-flow {
    border: 1px solid #e6e3df;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;
  }

  .flow-item {
    padding: 10px 14px;
    border-bottom: 1px solid #f1eee9;
  }

  .flow-item:last-child {
    border-bottom: none;
  }

  .flow-corps {
    background: #fbf8f3;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .corps-row-top {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .corps-row-info {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 12px;
    color: #555;
    padding-left: 34px;
  }

  .corps-choreo {
    font-style: italic;
  }

  .corps-cast {
    color: #6a4e2b;
  }

  .corps-song-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0;
  }

  .corps-song-input {
    max-width: 260px;
    margin: 0;
  }

  .corps-song-by {
    color: #888;
    font-size: 12px;
  }

  .corps-music-section {
    margin: 6px 0;
  }

  .corps-track-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    padding: 4px 6px;
    background: #fff;
    border-radius: 4px;
  }

  .corps-track-num {
    font-family: Quattrocento, Georgia, serif;
    font-weight: 700;
    font-size: 14px;
    color: #888;
    min-width: 18px;
  }

  .corps-music-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin: 6px 0;
  }

  .corps-audio {
    height: 32px;
    max-width: 260px;
  }

  .corps-music-filename {
    font-size: 11px;
    color: #888;
    font-style: italic;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .corps-music-error {
    color: #c62828;
    font-size: 11px;
    margin-top: 4px;
    display: block;
    width: 100%;
  }

  .corps-recording-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  .corps-recording-lab {
    font-size: 12px;
    color: #555;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .corps-recording-input {
    flex: 1;
    min-width: 220px;
    padding: 5px 8px;
    border: 1px solid #ccc;
    border-radius: 3px;
    font-size: 12px;
    font-family: ui-monospace, Menlo, monospace;
    background: #fff;
  }
  .rec-input {
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 3px;
    font-size: 12px;
    font-family: ui-monospace, Menlo, monospace;
    width: 240px;
    max-width: 100%;
    margin-right: 4px;
  }
  .rec-saved { color: #2e7d32; font-weight: 700; margin-left: 4px; }
  .vgt-table tr.row-corps td {
    background: #fbf8f3;
  }

  .corps-cast-groups {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .role-label {
    color: #5d4037;
    margin-right: 4px;
  }

  .flow-subtitle {
    color: #888;
    font-weight: 400;
    font-style: italic;
    margin-left: 6px;
    font-size: 14px;
  }

  .flow-intermission {
    background: #2b2b2b;
    color: #fff;
    text-align: center;
    font-style: italic;
    font-family: Quattrocento, Georgia, serif;
    letter-spacing: .04em;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .flow-intermission .intermission-label {
    font-size: 16px;
    font-weight: 700;
    color: #fff;
  }

  .flow-intermission .halftime {
    font-style: normal;
    color: #d8c89a;
    font-size: 13px;
    font-family: 'Fanwood Text', Georgia, serif;
    letter-spacing: 0;
  }

  .flow-block {
    background: #f3f1f8;
    padding: 12px 14px;
  }

  .flow-block-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }

  .flow-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #1a1a1a;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
  }

  .flow-title {
    font-family: Quattrocento, Georgia, serif;
    font-weight: 700;
    font-size: 16px;
    color: #1a1a1a;
  }

  .flow-tag {
    font-size: 11px;
    background: #eceff1;
    color: #455a64;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 600;
  }

  .block-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    background: #fff;
    border-radius: 4px;
  }

  .block-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f3f1ed;
    vertical-align: middle;
  }

  .block-table tr:last-child td {
    border-bottom: none;
  }

  .col-num {
    width: 28px;
    color: #999;
    font-weight: 600;
  }

  .sub {
    color: #888;
    font-size: 12px;
    margin-top: 2px;
  }

  .program-line {
    color: #1a1a1a;
    font-size: 13px;
  }

  .link {
    color: #267E7D;
    text-decoration: none;
    font-weight: 600;
  }

  .link:hover {
    text-decoration: underline;
  }

  .comp-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
  }

  .comp-yes {
    background: #fff8e1;
    color: #6a4e00;
  }

  .comp-no {
    background: #e8eaf6;
    color: #3949ab;
  }

  .tag-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }

  .type-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    background: #eceff1;
    color: #455a64;
  }

  .type-classical    { background: #e8eaf6; color: #283593; }
  .type-original     { background: #e0f2f1; color: #00695c; }
  .type-contemporary { background: #f3e5f5; color: #6a1b9a; }
  .type-jazz         { background: #ffe0b2; color: #e65100; }
  .type-default      { background: #eceff1; color: #455a64; }

  .inline-select {
    max-width: 140px;
    margin: 0;
  }

  .pin-input {
    max-width: 110px;
    margin: 0;
  }

  .pinned-marker {
    margin-right: 2px;
  }

  .block-table tr.is-pinned td {
    background: #fff8e1;
  }

  .pending-section {
    margin-top: 18px;
    padding: 12px 14px;
    border: 1px dashed #d0cbc4;
    border-radius: 6px;
    background: #fdfcfa;
  }

  .pending-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    gap: 12px;
  }

  .pending-section h4 {
    font-family: Quattrocento, Georgia, serif;
    font-weight: 700;
    font-size: 16px;
    color: #1a1a1a;
    margin: 0;
  }

  .pending-approvals {
    background: #fff8e8;
    border: 1.5px solid #e6c98a;
    border-radius: 12px;
    padding: 16px 18px;
  }
  .pending-title {
    font-family: Quattrocento, Georgia, serif;
    font-size: 20px;
    margin: 0 0 4px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .pending-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 8px;
    border-radius: 999px;
    background: #c4923a;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
  }
  .pending-lede {
    margin: 0 0 12px;
    font-size: 14px;
    color: #5c5a63;
  }
  .pending-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 0;
    border-top: 1px solid rgba(196, 146, 58, 0.35);
  }
  .pending-main {
    flex: 1;
    min-width: 200px;
    font-size: 15px;
  }
  .pending-meta {
    margin-top: 4px;
    font-size: 13px;
    color: #6b6860;
  }
  .pending-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
</style>

<script>
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import {
  resolveShowcaseStructure,
  resolveCorpsInfo,
  defaultRoleOrder
} from '@/utils/showcase-structure'
import {
  flattenShowcaseSequence,
  SHOWCASE_UNPLACED_SORT_BASE
} from '@/utils/showcase-order'


import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table'
import { db } from '@/firebase'
import { assertStageTimeOk } from '@/utils/iabf-stage-time'

  // Worst-case duration assumed for a solo whose lengthInSeconds isn't set yet.
  // Matches the assumption used by the existing runtime estimate (3:15).
  const WORST_CASE_SOLO_SECONDS = 60 * 3 + 15

  // Placeholder runtime for every corps piece until real rehearsal times are known.
  const CORPS_SECONDS = 4 * 60

  // Scheduled intermission length, included in total runtime.
  const INTERMISSION_SECONDS = 15 * 60

  // Corps participation fields / role order — year-specific (2026 repertoire
  // only). Other years resolve empty unless stored on showcase-runtime.
  // See src/utils/showcase-structure.js

  // Maps GW position prefix → role bucket. M*=Leaders/Men, P*=Pointe,
  // I*=Intermediate, B*=Flat (beginner flat).
  function gwRoleForPosition (position) {
    if (!position) return ''
    const prefix = String(position).charAt(0).toUpperCase()
    return { M: 'Leaders', P: 'Pointe', I: 'Intermediate', B: 'Flat' }[prefix] || ''
  }

  function buildCorpsCast (corpsName, dancers, lookups, corpsInfo, roleOrder) {
    const info = (corpsInfo || {})[corpsName]
    if (!info || !info.field) return []
    const choreographer = (info.choreographer || '').trim()
    const participating = dancers.filter(d => d && d[info.field] && d[info.field].participating)
    const sortByFirst = (a, b) => (a.firstName || '').localeCompare(b.firstName || '')
    const nameOf = d => `${d.firstName || ''} ${d.lastName || ''}`.trim()
    // Drop the choreographer so they don't appear in their own cast.
    const cast = participating.filter(d => nameOf(d) !== choreographer)
    const gwByDancer = (lookups && lookups.gwByDancer) || {}
    const becByDancer = (lookups && lookups.becByDancer) || {}
    let resolveRole = null
    // 2026-specific role resolution for known fields
    if (info.field === 'aliceInWonderland') {
      resolveRole = d => (d.aliceInWonderland && d.aliceInWonderland.role) || ''
    } else if (info.field === 'garlandWaltz') {
      resolveRole = d => gwRoleForPosition(gwByDancer[d.id])
    } else if (info.field === 'becoming') {
      resolveRole = d => becByDancer[d.id] || ''
    }
    if (!resolveRole) {
      return [{ role: '', dancers: cast.sort(sortByFirst).map(nameOf) }]
    }
    const order = (roleOrder || {})[corpsName] || []
    const groups = order.map(role => ({
      role,
      dancers: cast.filter(d => resolveRole(d) === role).sort(sortByFirst).map(nameOf)
    }))
    const known = new Set(order)
    const other = cast.filter(d => !known.has(resolveRole(d))).sort(sortByFirst).map(nameOf)
    if (other.length) groups.push({ role: 'Other', dancers: other })
    return groups.filter(g => g.dancers.length > 0)
  }


  function teacherCreditFor (piece) {
    const t = (piece && piece.programTeacher || '').trim()
    if (!t) return ''
    // If the admin already wrote the role/prefix, keep their wording.
    if (/(coached by|choreographed by|taught by|teacher\b|coach\b|choreographer\b|\bby \w)/i.test(t)) return t
    if (piece.pieceType === 'Classical Variation') return `coached by ${t}`
    return `choreographed by ${t}`
  }

  function formatDuration (totalSeconds) {
    const s = Math.max(0, Math.round(totalSeconds))
    if (s === 0) return '0 min'
    const hours = Math.floor(s / 3600)
    const mins = Math.round((s - hours * 3600) / 60)
    if (hours > 0 && mins > 0) return `${hours} hr ${mins} min`
    if (hours > 0) return `${hours} hr`
    return `${mins} min`
  }

  const BLOCK_OPTIONS = [
    { text: 'AA', value: 'AA' },
    { text: 'A', value: 'A' },
    { text: 'B', value: 'B' },
    { text: 'C', value: 'C' },
    { text: 'D', value: 'D' }
  ]

  const PLACEMENT_OPTIONS = [
    { text: 'Beginning', value: 'beginning' },
    { text: 'Middle', value: 'middle' },
    { text: 'End', value: 'end' }
  ]

  const PLACEMENT_RANK = { beginning: 1, middle: 2, end: 3 }

  function interleave (a, b) {
    const out = []
    const max = Math.max(a.length, b.length)
    for (let i = 0; i < max; i++) {
      if (i < a.length) out.push(a[i])
      if (i < b.length) out.push(b[i])
    }
    return out
  }

  function autoOrder (pieces) {
    // 1) Group by placement (beginning/middle/end/unspecified)
    // 2) Split unspecified in half so it cushions both sides of middle.
    // 3) Alternate competition and non-competition — top priority. The
    //    alternation carries ACROSS group boundaries: if the last
    //    beginning piece is competition, the first middle piece should
    //    be non-competition (when both sides have entries available).
    const groups = { beginning: [], middle: [], end: [], unspecified: [] }
    for (const p of pieces) {
      const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
      groups[k].push(p)
    }
    const unspecHalf = Math.ceil(groups.unspecified.length / 2)
    const groupSequence = [
      groups.beginning,
      groups.unspecified.slice(0, unspecHalf),
      groups.middle,
      groups.unspecified.slice(unspecHalf),
      groups.end
    ]
    const isComp = p => !!(p.competition && p.competition !== 'None')
    const out = []
    let lastType = null
    for (const list of groupSequence) {
      const comp = list.filter(isComp)
      const non = list.filter(p => !isComp(p))
      if (!comp.length && !non.length) continue
      let first, second
      if (!comp.length) { first = non; second = [] }
      else if (!non.length) { first = comp; second = [] }
      else if (lastType === 'comp') { first = non; second = comp }
      else if (lastType === 'non') { first = comp; second = non }
      else {
        // No prior context — lead with the longer side so leftover pieces
        // at the tail of the interleave are the more common type.
        first = comp.length >= non.length ? comp : non
        second = first === comp ? non : comp
      }
      const interleaved = interleave(first, second)
      out.push(...interleaved)
      if (interleaved.length) {
        lastType = isComp(interleaved[interleaved.length - 1]) ? 'comp' : 'non'
      }
    }
    return out
  }

  function orderBlockPieces (pieces) {
    if (!pieces.length) return []
    const pinnedNumeric = pieces
      .filter(p => Number.isFinite(p.showcaseOrder) && p.showcaseOrder >= 1)
      .slice()
      .sort((a, b) => a.showcaseOrder - b.showcaseOrder)
    const pinnedLast = pieces.filter(p => p.showcaseOrder === 'last')
    const unpinned = pieces.filter(p => !pinnedNumeric.includes(p) && !pinnedLast.includes(p))
    const N = pieces.length
    const slots = new Array(N).fill(null)
    // 1) Numeric pins claim their requested positions (ties + overflow push forward).
    for (const p of pinnedNumeric) {
      let target = Math.max(1, Math.min(p.showcaseOrder, N)) - 1
      while (target < N && slots[target] !== null) target += 1
      if (target >= N) target = slots.findIndex(s => s === null)
      if (target !== -1) slots[target] = p
    }
    // 2) "last" pins claim the last available slot (ties push earlier).
    for (const p of pinnedLast) {
      let target = N - 1
      while (target >= 0 && slots[target] !== null) target -= 1
      if (target === -1) target = slots.findIndex(s => s === null)
      if (target !== -1) slots[target] = p
    }
    // 3) Fill remaining slots so comp/non-comp alternates against the
    //    neighbours we already placed. Placement preference still orders
    //    each type queue (beg → unspec → mid → unspec → end), but the
    //    decision of WHICH queue to pull at each gap is driven by the
    //    type of the previous (or next) filled slot.
    const isComp = p => !!(p.competition && p.competition !== 'None')
    const placementOrder = (() => {
      const groups = { beginning: [], middle: [], end: [], unspecified: [] }
      for (const p of unpinned) {
        const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
        groups[k].push(p)
      }
      const half = Math.ceil(groups.unspecified.length / 2)
      return [
        ...groups.beginning,
        ...groups.unspecified.slice(0, half),
        ...groups.middle,
        ...groups.unspecified.slice(half),
        ...groups.end
      ]
    })()
    const compQueue = placementOrder.filter(isComp)
    const nonQueue = placementOrder.filter(p => !isComp(p))
    for (let i = 0; i < N; i++) {
      if (slots[i] !== null) continue
      let prevType = null
      for (let j = i - 1; j >= 0; j--) {
        if (slots[j] !== null) { prevType = isComp(slots[j]) ? 'comp' : 'non'; break }
      }
      let nextType = null
      if (prevType === null) {
        for (let j = i + 1; j < N; j++) {
          if (slots[j] !== null) { nextType = isComp(slots[j]) ? 'comp' : 'non'; break }
        }
      }
      let wantComp
      if (prevType === 'comp') wantComp = false
      else if (prevType === 'non') wantComp = true
      else if (nextType === 'comp') wantComp = false
      else if (nextType === 'non') wantComp = true
      else wantComp = compQueue.length >= nonQueue.length
      if (wantComp && compQueue.length) slots[i] = compQueue.shift()
      else if (!wantComp && nonQueue.length) slots[i] = nonQueue.shift()
      else if (compQueue.length) slots[i] = compQueue.shift()
      else if (nonQueue.length) slots[i] = nonQueue.shift()
    }
    return slots
  }

export default {
  mixins: [iabfAdminMixin],
    components: {
      VueGoodTable
    },
    data () {
      return {
        dancerLookup: {},
        dancerEmailLookup: {},
        view: 'table',
        approvingId: null,
        emailCopiedNote: '',
        missingMusicCopiedNote: '',
        publicLinkCopiedNote: '',
        orderFrozenAt: null,
        freezing: false,
        unfreezing: false,
        nowTick: Date.now(),
        nowTickHandle: null,
        corpsSongs: {},
        corpsMusic: {},
        corpsMusicUploading: null,
        corpsMusicPending: null,
        corpsMusicError: {},
        pieceMusicUploading: null,
        pieceMusicPending: null,
        pieceMusicError: {},
        gwByDancer: {},
        becByDancer: {},
        showcaseStructure: [],
        corpsInfoMap: {},
        roleOrder: {},
        blockOptions: BLOCK_OPTIONS,
        placementOptions: PLACEMENT_OPTIONS,
        columns: [
          {
            label: 'Show #',
            field: 'showcaseSort',
            type: 'number',
            width: '80px'
          },
          {
            label: 'Edit',
            field: 'edit'
          },
          {
            label: 'Song/Variation Title',
            field: 'title'
          },
          {
            label: 'Artist/Composer',
            field: 'songArtist'
          },
          {
            label: 'Dancers',
            field: 'namedDancers'
          },
          {
            label: 'Division',
            field: 'competition'
          },
          {
            label: 'Received Music?',
            field: 'musicReceived'
          },
          {
            label: 'Piece Length',
            field: 'lengthInMinutes'
          },
          {
            label: 'Piece Type',
            field: 'pieceType'
          },
          {
            label: 'Group Size',
            field: 'groupSize'
          },
          {
            label: 'Dancer Name Listed in Program',
            field: 'programDancer'
          },
          {
            label: 'Teacher/Choreographer for Program',
            field: 'programTeacher'
          },
          {
            label: 'Piece Title for the Program',
            field: 'programTitle'
          },
          {
            label: 'Performance recording',
            field: 'performanceRecordingUrl'
          }
        ],
        rows: [],
        recordingDrafts: {},
        recordingSaving: {},
        recordingSaved: {},
        unsubCorpsRecordings: null
      }
    },
    async created () {
      // Load dancers first so we can resolve dancer names into each row, which
      // lets vue-good-table's search match against the dancer column.
      await this.$store.dispatch('loadIABFDancers')
      this.$store.getters.loadedIABFDancers.forEach(dancer => {
        this.dancerLookup[dancer.id] = dancer.firstName + ' ' + dancer.lastName
        if (dancer.email) this.dancerEmailLookup[dancer.id] = dancer.email
      })
      // Per-piece role data lives in iabf-gw-videos / iabf-becoming-videos.
      // Admin can read both; we use these to group the cast on the lineup.
      try {
        const [gwSnap, becSnap] = await Promise.all([
          db.collection(this.IABF.gwVideos).get(),
          db.collection(this.IABF.becomingVideos).get()
        ])
        // Build new objects and assign so Vue's reactivity catches the change
        // (adding keys to a tracked object isn't reactive in Vue 2).
        const gwLookup = {}
        gwSnap.forEach(doc => {
          const data = doc.data() || {}
          if (data.dancerId && data.position) gwLookup[data.dancerId] = data.position
        })
        this.gwByDancer = gwLookup
        const becLookup = {}
        becSnap.forEach(doc => {
          const data = doc.data() || {}
          if (data.dancerId && data.ensemble) becLookup[data.dancerId] = data.ensemble
        })
        this.becByDancer = becLookup
      } catch (e) {
        console.warn('Could not load video collections for role grouping:', e)
      }
      await this.$store.dispatch('loadIABFPieces', { useAdminYear: true })
      this.$store.getters.loadedIABFPieces.forEach((piece) => {
        const dancers = Array.isArray(piece.dancers) ? piece.dancers : []
        const names = dancers.map(id => this.dancerLookup[id]).filter(Boolean)
        // Sort by first name so multi-dancer pieces read consistently.
        names.sort((a, b) => (a.split(' ')[0] || '').localeCompare(b.split(' ')[0] || ''))
        const computed = names.join(', ')
        this.rows.push({
          ...piece,
          namedDancers: computed
        })
        // Backfill: if the stored namedDancers is stale or missing, write the
        // resolved string back so the public lineup (which can't read the
        // dancers collection) has a dancer name to display.
        if (computed && (piece.namedDancers || '') !== computed) {
          this.$store.dispatch('setPieceNamedDancers', { pieceId: piece.id, namedDancers: computed })
            .catch(e => console.warn('namedDancers sync failed:', piece.id, e))
        }
      })
      // Publish corps casts to the public config doc so the public lineup can
      // show them without reading the dancers collection.
      this.$store.dispatch('publishCorpsCasts', { corpsCasts: this.corpsCasts })
        .catch(e => console.warn('corpsCasts publish failed:', e))
      // Load the showcase config so the corps song/artist inputs are pre-filled.
      // Year defaults first (2026 includes corps; other years solo blocks only).
      this.applyShowcaseConfig({})
      this.$store.dispatch('loadShowcaseConfig').then(cfg => {
        this.corpsSongs = cfg.corpsSongs || {}
        this.corpsMusic = cfg.corpsMusic || {}
        this.orderFrozenAt = cfg.orderFrozenAt || null
        this.applyShowcaseConfig(cfg)
      }).catch(() => {})
      // Refresh the "Frozen 5 min ago" tooltip without re-rendering the
      // whole lineup.
      this.nowTickHandle = setInterval(() => { this.nowTick = Date.now() }, 60000)
      // Performance recording URLs for the five corps pieces live in a
      // separate config doc; subscribe so the inputs reflect current
      // values across admin sessions.
      this.unsubCorpsRecordings = await this.$store.dispatch('subscribeCorpsRecordings', { useAdminYear: true })
    },
    beforeDestroy () {
      if (this.nowTickHandle) clearInterval(this.nowTickHandle)
      if (typeof this.unsubCorpsRecordings === 'function') this.unsubCorpsRecordings()
    },
    computed: {
      corpsCasts () {
        const dancers = this.$store.getters.loadedIABFDancers || []
        const out = {}
        const lookups = { gwByDancer: this.gwByDancer, becByDancer: this.becByDancer }
        for (const name of Object.keys(this.corpsInfoMap || {})) {
          out[name] = buildCorpsCast(name, dancers, lookups, this.corpsInfoMap, this.roleOrder)
        }
        return out
      },
      corpsCastTotal () {
        return name => (this.corpsCasts[name] || []).reduce((acc, g) => acc + g.dancers.length, 0)
      },
      frozenAgoText () {
        if (!this.orderFrozenAt) return ''
        const diff = Math.max(0, this.nowTick - this.orderFrozenAt)
        const mins = Math.floor(diff / 60000)
        if (mins < 1) return 'just now'
        if (mins < 60) return mins + ' min ago'
        const hrs = Math.floor(mins / 60)
        if (hrs < 24) return hrs + 'h ago'
        const days = Math.floor(hrs / 24)
        return days + 'd ago'
      },
      pendingPieces () {
        return this.rows
          .filter(p => p.approvalStatus === 'pending')
          .slice()
          .sort((a, b) => this.submittedMillis(a) - this.submittedMillis(b))
      },
      /** Approved (or legacy) pieces for lineup / table — not pending submissions. */
      approvedRows () {
        return this.rows.filter(p => (p.approvalStatus || 'approved') !== 'pending')
      },
      assignedPieces () {
        return this.approvedRows.filter(p => p.showcaseBlock)
      },
      unassignedPieces () {
        return this.approvedRows
          .filter(p => !p.showcaseBlock)
          .slice()
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
      },
      publicLineupUrl () {
        return window.location.origin + '/showcase'
      },
      showRunnerUrl () {
        return window.location.origin + '/iabf/showrunner'
      },
      piecesMissingMusic () {
        return this.approvedRows.filter(p => !p.musicReceived)
      },
      missingMusicEmails () {
        const seen = new Set()
        const out = []
        for (const p of this.piecesMissingMusic) {
          const dancers = Array.isArray(p.dancers) ? p.dancers : []
          for (const id of dancers) {
            const email = this.dancerEmailLookup[id]
            if (email && !seen.has(email.toLowerCase())) {
              seen.add(email.toLowerCase())
              out.push(email)
            }
          }
        }
        return out
      },
      unassignedSeconds () {
        return this.unassignedPieces.reduce(
          (acc, p) => acc + (parseInt(p.lengthInSeconds, 10) || WORST_CASE_SOLO_SECONDS),
          0
        )
      },
      solosRuntimeSeconds () {
        // All solos: those placed in a block (counted by blockSeconds inside
        // halfSeconds) plus those still awaiting a block.
        return BLOCK_OPTIONS.reduce((acc, b) => acc + this.blockSeconds(b.value), 0) + this.unassignedSeconds
      },
      corpsRuntimeSeconds () {
        return this.showcaseStructure
          .filter(slot => slot.type === 'corps')
          .reduce((acc, slot) => acc + this.corpsSecondsFor(slot.name), 0)
      },
      totalRuntimeSeconds () {
        return this.solosRuntimeSeconds + this.corpsRuntimeSeconds + INTERMISSION_SECONDS
      },
      totalRuntimeText () {
        return formatDuration(this.totalRuntimeSeconds)
      },
      runtimeBreakdownText () {
        return `solos ${formatDuration(this.solosRuntimeSeconds)} + corps ${formatDuration(this.corpsRuntimeSeconds)} + interval ${formatDuration(INTERMISSION_SECONDS)}`
      },
      unassignedEmails () {
        const seen = new Set()
        const out = []
        for (const p of this.unassignedPieces) {
          const dancers = Array.isArray(p.dancers) ? p.dancers : []
          for (const id of dancers) {
            const email = this.dancerEmailLookup[id]
            if (email && !seen.has(email.toLowerCase())) {
              seen.add(email.toLowerCase())
              out.push(email)
            }
          }
        }
        return out
      },
      /** Table view: lineup sequence (solos + corps) then unassigned / pending. */
      tableRows () {
        const byBlock = {}
        for (const p of this.approvedRows) {
          if (!p.showcaseBlock) {
            continue
          }
          if (!byBlock[p.showcaseBlock]) {
            byBlock[p.showcaseBlock] = []
          }
          byBlock[p.showcaseBlock].push(p)
        }
        const seq = flattenShowcaseSequence(this.showcaseStructure, byBlock)
        const placedIds = new Set()
        const rows = []
        seq.forEach((item, idx) => {
          const sort = idx + 1
          if (item.kind === 'corps') {
            rows.push(this.buildCorpsTableRow(item.name, sort))
            return
          }
          if (item.piece) {
            placedIds.add(item.piece.id)
            rows.push({
              ...item.piece,
              isCorps: false,
              showcaseSort: sort
            })
          }
        })
        let extra = SHOWCASE_UNPLACED_SORT_BASE
        this.approvedRows.filter(p => !placedIds.has(p.id)).forEach(p => {
          extra += 1
          rows.push({ ...p, isCorps: false, showcaseSort: extra })
        })
        this.pendingPieces.forEach(p => {
          extra += 1
          rows.push({ ...p, isCorps: false, showcaseSort: extra })
        })
        return rows
      }
    },
    methods: {
      submittedMillis (piece) {
        const raw = (piece && (piece.submittedAt || piece.createdAt)) || null
        if (!raw) {
          return Number.MAX_SAFE_INTEGER
        }
        if (typeof raw.toMillis === 'function') {
          return raw.toMillis()
        }
        if (typeof raw.toDate === 'function') {
          return raw.toDate().getTime()
        }
        if (typeof raw.seconds === 'number') {
          return raw.seconds * 1000
        }
        const n = new Date(raw).getTime()
        return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER
      },
      formatSubmittedAt (piece) {
        const ms = this.submittedMillis(piece)
        if (!Number.isFinite(ms) || ms === Number.MAX_SAFE_INTEGER) {
          return ''
        }
        return new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        }).format(new Date(ms))
      },
      applyShowcaseConfig (cfg) {
        const year = this.adminFestivalYear || this.IABF.year
        this.showcaseStructure = resolveShowcaseStructure(year, cfg || {})
        this.corpsInfoMap = resolveCorpsInfo(year, cfg || {})
        this.roleOrder = defaultRoleOrder(year)
      },
      piecesInBlock (block) {
        const inBlock = this.approvedRows.filter(p => p.showcaseBlock === block)
        return orderBlockPieces(inBlock)
      },
      async approvePiece (piece) {
        if (!piece || !piece.id) return
        this.approvingId = piece.id
        try {
          await this.$store.dispatch('approveShowcasePiece', { pieceId: piece.id })
          const idx = this.rows.findIndex(r => r.id === piece.id)
          if (idx !== -1) {
            this.$set(this.rows, idx, {
              ...this.rows[idx],
              approvalStatus: 'approved',
              approvedAt: new Date()
            })
          }
        } catch (e) {
          console.error('Approve piece failed:', e)
          // eslint-disable-next-line no-alert
          window.alert('Could not approve piece: ' + (e.message || e))
        }
        this.approvingId = null
      },
      // ─── Performance recording URLs (per-piece + corps) ────────────
      tableRowClass (row) {
        return row && row.isCorps ? 'row-corps' : ''
      },
      formatShowcaseSort (value) {
        const n = Number(value)
        if (!Number.isFinite(n) || n >= SHOWCASE_UNPLACED_SORT_BASE) {
          return '—'
        }
        return n
      },
      buildCorpsTableRow (name, sort) {
        const info = this.corpsInfo(name)
        const song = this.corpsSongs[name] || {}
        const tracks = this.corpsTracks(name)
        const lengthInSeconds = tracks.reduce(
          (acc, t) => acc + (parseInt(t.lengthInSeconds, 10) || 0),
          0
        )
        const recordings = this.$store.getters.corpsRecordings || {}
        const field = info.field || ''
        const castNames = []
        ;(this.corpsCasts[name] || []).forEach(group => {
          (group.dancers || []).forEach(d => {
            if (d) {
              castNames.push(d)
            }
          })
        })
        return {
          id: 'corps:' + (field || name),
          isCorps: true,
          corpsField: field,
          title: name,
          songArtist: song.artist || '',
          namedDancers: castNames.length ? castNames.join(', ') : 'Corps de Ballet',
          dancers: [],
          competition: '',
          musicReceived: tracks.length > 0,
          musicUrl: '',
          musicFileName: (tracks[0] && tracks[0].fileName) || '',
          lengthInSeconds: lengthInSeconds || 0,
          pieceType: 'Corps de Ballet',
          groupSize: 'Corps',
          programDancer: 'Corps de Ballet',
          programTeacher: info.choreographer || '',
          programTitle: song.title ? (name + ' — ' + song.title) : name,
          performanceRecordingUrl: field ? (recordings[field] || '') : '',
          showcaseSort: sort
        }
      },
      recordingDraftFor (row) {
        if (!row) return ''
        const draft = this.recordingDrafts[row.id]
        if (draft !== undefined) {
          return draft
        }
        if (row.isCorps) {
          const saved = (this.$store.getters.corpsRecordings || {})[row.corpsField] || ''
          return saved
        }
        return row.performanceRecordingUrl || ''
      },
      onRecordingInput (pieceId, value) {
        this.$set(this.recordingDrafts, pieceId, value)
      },
      recordingChanged (row) {
        if (!row) return false
        const draft = this.recordingDrafts[row.id]
        if (draft === undefined) return false
        const saved = row.isCorps
          ? ((this.$store.getters.corpsRecordings || {})[row.corpsField] || '')
          : (row.performanceRecordingUrl || '')
        return draft.trim() !== saved.trim()
      },
      async saveRowRecording (row) {
        if (!row) return
        if (row.isCorps) {
          await this.saveCorpsRecording(row.corpsField)
          return
        }
        await this.savePieceRecording(row)
      },
      async savePieceRecording (row) {
        const key = row.id
        const url = (this.recordingDrafts[key] || '').trim()
        this.$set(this.recordingSaving, key, true)
        try {
          await this.$store.dispatch('setPiecePerformanceRecording', { pieceId: key, url, useAdminYear: true })
          const idx = this.rows.findIndex(r => r.id === key)
          if (idx !== -1) {
            this.$set(this.rows[idx], 'performanceRecordingUrl', url)
          }
          this.$delete(this.recordingDrafts, key)
          this.$set(this.recordingSaved, key, true)
          setTimeout(() => { this.$delete(this.recordingSaved, key) }, 1800)
        } catch (e) {
          console.error('Save piece recording failed:', e)
          // eslint-disable-next-line no-alert
          window.alert('Save failed: ' + (e.message || e))
        }
        this.$delete(this.recordingSaving, key)
      },
      corpsRecordingDraft (field) {
        if (!field) return ''
        const draftKey = 'corps:' + field
        const draft = this.recordingDrafts[draftKey]
        const saved = (this.$store.getters.corpsRecordings || {})[field] || ''
        return draft !== undefined ? draft : saved
      },
      onCorpsRecordingInput (field, value) {
        if (!field) return
        this.$set(this.recordingDrafts, 'corps:' + field, value)
      },
      corpsRecordingChanged (field) {
        if (!field) return false
        const draftKey = 'corps:' + field
        const draft = this.recordingDrafts[draftKey]
        if (draft === undefined) return false
        const saved = (this.$store.getters.corpsRecordings || {})[field] || ''
        return draft.trim() !== saved.trim()
      },
      async saveCorpsRecording (field) {
        if (!field) return
        const draftKey = 'corps:' + field
        const url = (this.recordingDrafts[draftKey] || '').trim()
        this.$set(this.recordingSaving, draftKey, true)
        try {
          await this.$store.dispatch('setCorpsRecording', { field, url, useAdminYear: true })
          this.$delete(this.recordingDrafts, draftKey)
          this.$set(this.recordingSaved, draftKey, true)
          setTimeout(() => { this.$delete(this.recordingSaved, draftKey) }, 1800)
        } catch (e) {
          console.error('Save corps recording failed:', e)
          // eslint-disable-next-line no-alert
          window.alert('Save failed: ' + (e.message || e))
        }
        this.$delete(this.recordingSaving, draftKey)
      },
      async onFreezeOrder () {
        const count = this.rows.filter(p => p.showcaseBlock).length
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Freeze the show order for ${count} pieces? Auto-ordering will stop until you unfreeze. New pieces added later will land at the end of their block. Pieces marked "last" stay anchored to the end.`)) return
        this.freezing = true
        try {
          const blocks = {}
          for (const block of (BLOCK_OPTIONS || []).map(o => o.value).filter(Boolean)) {
            const ordered = this.piecesInBlock(block)
            if (ordered.length) blocks[block] = ordered
          }
          await this.$store.dispatch('freezeShowcaseOrder', { blocksOfPieces: blocks })
          this.orderFrozenAt = Date.now()
        } catch (e) {
          console.error('freeze failed:', e)
          // eslint-disable-next-line no-alert
          window.alert('Could not freeze: ' + (e && e.message ? e.message : e))
        }
        this.freezing = false
      },
      async onUnfreezeOrder () {
        // eslint-disable-next-line no-alert
        if (!window.confirm('Unfreeze the show order? Auto-ordering will resume and pieces may shuffle.')) return
        this.unfreezing = true
        try {
          await this.$store.dispatch('unfreezeShowcaseOrder')
          this.orderFrozenAt = null
        } catch (e) {
          console.error('unfreeze failed:', e)
          // eslint-disable-next-line no-alert
          window.alert('Could not unfreeze: ' + (e && e.message ? e.message : e))
        }
        this.unfreezing = false
      },
      corpsInfo (name) {
        return this.corpsInfoMap[name] || { choreographer: '', field: '' }
      },
      teacherCredit (piece) {
        return teacherCreditFor(piece)
      },
      pieceTypeLabel (piece) {
        const t = piece && piece.pieceType
        if (!t) return ''
        if (t.startsWith('Classical')) return 'Classical'
        if (t.startsWith('Original')) return 'Original Ballet'
        if (t.startsWith('Contemporary')) return 'Contemporary/Lyrical'
        if (t.startsWith('Jazz')) return 'Jazz'
        return t
      },
      pieceTypeClass (piece) {
        const label = this.pieceTypeLabel(piece)
        if (label === 'Classical') return 'type-classical'
        if (label === 'Original Ballet') return 'type-original'
        if (label === 'Contemporary/Lyrical') return 'type-contemporary'
        if (label === 'Jazz') return 'type-jazz'
        return 'type-default'
      },
      competitionLabel (piece) {
        if (!piece || !piece.competition || piece.competition === 'None') return ''
        return piece.competition
      },
      setCorpsSong (name, field, value) {
        const current = { ...(this.corpsSongs[name] || {}) }
        current[field] = value || ''
        this.$set(this.corpsSongs, name, current)
        this.$store.dispatch('setCorpsSongField', { name, field, value: value || '' })
          .catch(e => console.warn('corps song save failed:', e))
      },
      corpsTracks (name) {
        const entry = this.corpsMusic[name]
        if (!entry) return []
        // New shape: { tracks: [...] }. Legacy shape: { url, fileName, ... } → wrap.
        if (Array.isArray(entry.tracks)) return entry.tracks
        if (entry.url) return [{ url: entry.url, fileName: entry.fileName || '', lengthInSeconds: entry.lengthInSeconds || 0 }]
        return []
      },
      async saveCorpsTracks (name, tracks) {
        await this.$store.dispatch('setCorpsMusicTracks', { name, tracks })
        this.$set(this.corpsMusic, name, { tracks })
      },
      async pickCorpsMusic (name) {
        this.corpsMusicPending = name
        this.$set(this.corpsMusicError, name, '')
        await this.$nextTick()
        if (this.$refs.corpsFileInput) {
          this.$refs.corpsFileInput.value = ''
          this.$refs.corpsFileInput.click()
        }
      },
      async onCorpsFileChosen (event) {
        const file = event.target.files && event.target.files[0]
        const name = this.corpsMusicPending
        this.corpsMusicPending = null
        if (!file || !name) return
        this.corpsMusicUploading = name
        this.$set(this.corpsMusicError, name, '')
        try {
          const ext = (file.name.split('.').pop() || '').toLowerCase()
          if (!['mp3', 'm4a', 'wav'].includes(ext)) {
            throw new Error('Please choose an .mp3, .m4a, or .wav file.')
          }
          const seconds = await this.readAudioDuration(file)
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_')
          const sanitizedKey = name.replace(/[^a-zA-Z0-9._-]+/g, '_')
          const { storage } = await import('@/firebase')
          const idx = this.corpsTracks(name).length
          const path = `iabf-music-2026/corps-${sanitizedKey}-${idx + 1}-${safeName}`
          const ref = storage.ref(path)
          const snap = await ref.put(file, { contentType: file.type || 'audio/mpeg' })
          const url = await snap.ref.getDownloadURL()
          const track = { url, fileName: file.name, lengthInSeconds: seconds }
          const tracks = [...this.corpsTracks(name), track]
          await this.saveCorpsTracks(name, tracks)
        } catch (e) {
          console.error('Corps music upload failed:', e)
          this.$set(this.corpsMusicError, name, e.message || 'Upload failed — please try again.')
        }
        this.corpsMusicUploading = null
      },
      async moveCorpsTrack (name, index, delta) {
        const tracks = [...this.corpsTracks(name)]
        const target = index + delta
        if (target < 0 || target >= tracks.length) return
        const [moved] = tracks.splice(index, 1)
        tracks.splice(target, 0, moved)
        await this.saveCorpsTracks(name, tracks)
      },
      async removeCorpsTrack (name, index) {
        const tracks = this.corpsTracks(name)
        const track = tracks[index]
        if (!track) return
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Remove track ${index + 1} (${track.fileName || ''}) from "${name}"?`)) return
        if (track.url) {
          const path = this.storagePathFromUrl(track.url)
          if (path) {
            try {
              const { storage } = await import('@/firebase')
              await storage.ref(path).delete()
            } catch (e) {
              console.warn('Could not delete storage file (continuing):', e)
            }
          }
        }
        const remaining = tracks.filter((_, i) => i !== index)
        await this.saveCorpsTracks(name, remaining)
      },
      async pickPieceMusic (piece) {
        this.pieceMusicPending = piece
        this.$set(this.pieceMusicError, piece.id, '')
        await this.$nextTick()
        if (this.$refs.pieceMusicInput) {
          this.$refs.pieceMusicInput.value = ''
          this.$refs.pieceMusicInput.click()
        }
      },
      async onPieceMusicChosen (event) {
        const file = event.target.files && event.target.files[0]
        const piece = this.pieceMusicPending
        this.pieceMusicPending = null
        if (!file || !piece) return
        this.pieceMusicUploading = piece.id
        this.$set(this.pieceMusicError, piece.id, '')
        try {
          const ext = (file.name.split('.').pop() || '').toLowerCase()
          if (!['mp3', 'm4a', 'wav'].includes(ext)) {
            throw new Error('Please choose an .mp3, .m4a, or .wav file.')
          }
          const seconds = await this.readAudioDuration(file)
          if (seconds <= 0) {
            throw new Error('Could not read the audio length — please try a different file.')
          }
          const check = assertStageTimeOk({
            allPieces: this.rows,
            proposedPiece: { ...piece, lengthInSeconds: seconds },
            nameForId: (id) => this.dancerLookup[id] || ''
          })
          if (!check.ok) {
            throw new Error(check.error)
          }
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_')
          const path = `iabf-music-2026/${piece.id}-${safeName}`
          const { storage } = await import('@/firebase')
          const ref = storage.ref(path)
          const snap = await ref.put(file, { contentType: file.type || 'audio/mpeg' })
          const downloadUrl = await snap.ref.getDownloadURL()
          await db.collection(this.IABF.pieces).doc(piece.id).update({
            musicUrl: downloadUrl,
            musicFileName: file.name,
            musicReceived: true,
            lengthInSeconds: seconds
          })
          const idx = this.rows.findIndex(r => r.id === piece.id)
          if (idx !== -1) {
            this.$set(this.rows, idx, {
              ...this.rows[idx],
              musicUrl: downloadUrl,
              musicFileName: file.name,
              musicReceived: true,
              lengthInSeconds: seconds
            })
          }
        } catch (e) {
          console.error('Piece music upload failed:', e)
          this.$set(this.pieceMusicError, piece.id, e.message || 'Upload failed — please try again.')
        }
        this.pieceMusicUploading = null
      },
      readAudioDuration (file) {
        return new Promise((resolve, reject) => {
          const objectUrl = URL.createObjectURL(file)
          const audio = new Audio()
          const cleanup = () => { try { URL.revokeObjectURL(objectUrl) } catch (e) { /* noop */ } }
          const timeout = setTimeout(() => { cleanup(); reject(new Error('Audio metadata took too long.')) }, 12000)
          audio.addEventListener('loadedmetadata', () => {
            clearTimeout(timeout)
            cleanup()
            const sec = Number(audio.duration)
            resolve(Number.isFinite(sec) ? Math.round(sec) : 0)
          })
          audio.addEventListener('error', () => {
            clearTimeout(timeout)
            cleanup()
            reject(new Error('Could not read the audio file.'))
          })
          audio.src = objectUrl
        })
      },
      storagePathFromUrl (url) {
        if (!url) return null
        const fb = url.match(/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)/)
        if (fb) return decodeURIComponent(fb[1])
        const gs = url.match(/storage\.googleapis\.com\/[^/]+\/(.+)$/)
        if (gs) return decodeURIComponent(gs[1])
        return null
      },
      blockSeconds (block) {
        return this.rows
          .filter(p => p.showcaseBlock === block)
          .reduce((acc, p) => acc + (parseInt(p.lengthInSeconds, 10) || WORST_CASE_SOLO_SECONDS), 0)
      },
      blockSummary (block) {
        const pieces = this.rows.filter(p => p.showcaseBlock === block)
        const n = pieces.length
        if (n === 0) return ''
        return `${n} solo${n === 1 ? '' : 's'} · ~${formatDuration(this.blockSeconds(block))}`
      },
      corpsSecondsFor (name) {
        const tracks = this.corpsTracks(name)
        const total = tracks.reduce((acc, t) => acc + (parseInt(t.lengthInSeconds, 10) || 0), 0)
        return total > 0 ? total : CORPS_SECONDS
      },
      halfSeconds (half) {
        return this.showcaseStructure
          .filter(slot => slot.half === half)
          .reduce((acc, slot) => {
            if (slot.type === 'soloBlock') return acc + this.blockSeconds(slot.block)
            if (slot.type === 'corps') return acc + this.corpsSecondsFor(slot.name)
            return acc
          }, 0)
      },
      halfSummary (half) {
        return `~${formatDuration(this.halfSeconds(half))}`
      },
      isCompetition (piece) {
        return !!(piece.competition && piece.competition !== 'None')
      },
      isPinned (piece) {
        if (piece.showcaseOrder === 'last') return true
        return Number.isFinite(piece.showcaseOrder) && piece.showcaseOrder >= 1
      },
      programTitleFor (piece) {
        if (piece.programTitle) return piece.programTitle
        const title = piece.title || '(untitled)'
        if (piece.songArtist) return `${title} by ${piece.songArtist}`
        return title
      },
      programDancerFor (piece) {
        return piece.programDancer || piece.namedDancers || ''
      },
      async copyPublicLineupUrl () {
        try {
          await navigator.clipboard.writeText(this.publicLineupUrl)
          this.publicLinkCopiedNote = '✓ Copied'
        } catch (e) {
          this.publicLinkCopiedNote = 'Copy failed'
        }
        setTimeout(() => { this.publicLinkCopiedNote = '' }, 2000)
      },
      async copyUnassignedEmails () {
        const text = this.unassignedEmails.join(', ')
        try {
          await navigator.clipboard.writeText(text)
          this.emailCopiedNote = `✓ Copied ${this.unassignedEmails.length}`
        } catch (e) {
          this.emailCopiedNote = 'Copy failed'
        }
        setTimeout(() => { this.emailCopiedNote = '' }, 2000)
      },
      async copyMissingMusicEmails () {
        const text = this.missingMusicEmails.join(', ')
        try {
          await navigator.clipboard.writeText(text)
          this.missingMusicCopiedNote = `✓ Copied ${this.missingMusicEmails.length}`
        } catch (e) {
          this.missingMusicCopiedNote = 'Copy failed'
        }
        setTimeout(() => { this.missingMusicCopiedNote = '' }, 2000)
      },
      async updateAssignment (pieceId, partial) {
        // Optimistic local update so the show order recomputes immediately.
        const row = this.rows.find(r => r.id === pieceId)
        if (!row) return
        const prev = {
          showcaseBlock: row.showcaseBlock,
          showcasePlacement: row.showcasePlacement,
          showcaseOrder: row.showcaseOrder
        }
        const apply = {}
        if ('showcaseBlock' in partial) apply.showcaseBlock = partial.showcaseBlock || null
        if ('showcasePlacement' in partial) apply.showcasePlacement = partial.showcasePlacement || null
        if ('showcaseOrder' in partial) {
          const raw = partial.showcaseOrder
          if (raw === null || raw === '' || raw === undefined) {
            apply.showcaseOrder = null
          } else if (typeof raw === 'string' && raw.trim().toLowerCase() === 'last') {
            apply.showcaseOrder = 'last'
          } else {
            const n = Number(raw)
            apply.showcaseOrder = (Number.isFinite(n) && n >= 1) ? n : null
          }
        }
        for (const k of Object.keys(apply)) this.$set(row, k, apply[k])
        try {
          await this.$store.dispatch('updatePieceShowcaseAssignment', {
            pieceId,
            ...apply
          })
        } catch (e) {
          console.error('updateAssignment failed:', e)
          for (const k of Object.keys(apply)) this.$set(row, k, prev[k])
        }
      },
      timeInMinutes (seconds) {
        return Math.floor(seconds / 60) + ':' + (seconds % 60).toString().padStart(2, '0')
      },
      timeInHours (seconds) {
        const hours = (seconds / 60 / 60)
        return hours.toFixed(0) + ' hour ' + Math.floor((hours % 1) * 60) + ' minutes'
      },
      listDancerNames (dancers) {
        return dancers.map(dancer => {
          return this.dancerLookup[dancer]
        }).join(', ')
      }
    }
  }
</script>
